import { Funnel, Info, PhoneSlash, Robot } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { Badge, BadgeVariant } from 'src/components/Badge';
import { Button } from 'src/components/Button';
import { CallErrorDisplay } from 'src/components/CallErrorDisplay';
import { CallFiltersModal } from 'src/components/CallFiltersModal';
import { CopyableField } from 'src/components/CopyableField';
import { Label } from 'src/components/Label';
import Table from 'src/components/Table';
import { Tooltip } from 'src/components/Tooltip';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, Subject } from 'src/hooks/usePermissions';
import { Agent } from 'src/interfaces/agent.interface';
import { Call, CallFilters } from 'src/interfaces/call.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { CallStatus } from 'src/stubs/calls.stub';
import { friendlyDateFormatter } from 'src/utils/date';
import { processPhoneNumber } from 'src/utils/number';
import { CallFeedbackSidebar } from './components/CallFeedbackSidebar';
import { useRightHandSidebar } from 'src/contexts/RightHandSidebarContext';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Input } from 'src/components/Input';

const headers = [
  { key: 'id', label: 'ID', width: '6rem' },
  { key: 'agent', label: 'Agent', width: '6rem', disableSorting: true },
  { key: 'created_at', label: 'Created At', width: '15%' },
  {
    key: 'call_participants',
    label: 'Call Participants',
    width: '25%',
    wrapValue: false,
    disableSorting: true,
  },
];

const agentNameCache: Record<string, string> = {};

const FLUENTS_CONTEXT_PROPERTY_PREFIX = '$fluents';

const removeFluentsPropertiesFromCall = (call: Call) => {
  const context = { ...call.context };
  Object.keys(context).forEach((key) => {
    if (key.startsWith(FLUENTS_CONTEXT_PROPERTY_PREFIX)) {
      delete context[key];
    }
  });

  return {
    ...call,
    context,
  };
};

export const CallFeedback = () => {
  const { get } = useAuthedRequest();
  const notification = useNotification();
  const { openSidebar } = useRightHandSidebar();

  const [agents, setAgents] = useState([]);
  const [isCallFeedbackModalOpen, setIsCallFeedbackModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [rawCalls, setRawCalls] = useState<Call[]>([]);
  const [loading, setLoading] = useState(false);
  const [size, setSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [filters, setFilters] = useState<CallFilters>(() => {
    return {
      agent: '',
      call_id: '',
      contact_number: '',
      start_time: null,
      end_time: null,
      duration: { comparison: 'gt', value: 30 },
      successful_actions: [],
      transcript_keyword: '',
    };
  });

  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string | null;
  }>({ key: null, direction: null });

  const fetchData = async (page: number, perPage: number) => {
    setLoading(true);

    const formattedFilters: CallFilters = {
      ...filters,
      ...(filters.status === 'is_not_voicemail' && {
        status: undefined,
        is_voicemail: false,
      }),
      ...(filters.successful_actions?.length === 0 && {
        successful_actions: undefined,
      }),
      ...(filters.agent === '' && { agent_id: undefined }),
      ...(filters.call_id === '' && { call_id: undefined }),
    };

    const queryObject: QueryObject = {
      page: String(page),
      size: String(perPage),
      filters: JSON.stringify(formattedFilters),
    };

    if (sortConfig.key) {
      queryObject.sort_column = sortConfig.key;
      queryObject.sort_desc =
        sortConfig.direction === 'desc' ? 'true' : 'false';
    }

    const query = new URLSearchParams(queryObject as Record<string, string>);

    const fetchResult = await get(`/calls?${query.toString()}`);
    const processedCalls = fetchResult.items.map((call: Call) =>
      removeFluentsPropertiesFromCall(call),
    );
    setRawCalls(processedCalls);

    const uniqueAgents: string[] = Array.from(
      new Set(
        processedCalls.map((call: Call) =>
          typeof call.agent === 'object' ? call.agent.id : call.agent,
        ),
      ),
    );
    await Promise.all(
      uniqueAgents.map(async (uniqueAgent) => {
        if (!agentNameCache[uniqueAgent]) {
          try {
            const agent = await get(`/agents/${uniqueAgent}`);
            agentNameCache[uniqueAgent] = agent.name;
          } catch {
            agentNameCache[uniqueAgent] = '(deleted)';
          }
        }
      }),
    );

    const calls = await Promise.all(
      processedCalls.map(async (call: Call) => {
        const startDate = call.start_time
          ? new Date(Number(call.start_time) * 1000)
          : null;

        return {
          id: <CopyableField value={call.id} notification={notification} />,
          agent: (
            <div>
              <CopyableField
                value={
                  typeof call.agent === 'object' ? call.agent.id : call.agent
                }
                notification={notification}
              />
              <div>
                {typeof call.agent === 'object'
                  ? agentNameCache[call.agent.id]
                  : agentNameCache[call.agent]}
              </div>
            </div>
          ),
          created_at: friendlyDateFormatter(startDate),
          call_participants: (
            <div>
              <div className="flex justify-between">
                <Label>From:</Label>

                <div className="flex gap-2">
                  {call.is_outgoing && (
                    <Tooltip content="Voice Agent" position="top">
                      <Robot className="bold w-5 h-5" />
                    </Tooltip>
                  )}

                  {((call.is_outgoing &&
                    call.stage_outcome === 'bot_disconnected') ||
                    (!call.is_outgoing &&
                      call.stage_outcome === 'human_disconnected')) && (
                    <Tooltip content="Ended the call" position="top">
                      <PhoneSlash className="bold w-5 h-5" />
                    </Tooltip>
                  )}
                  {processPhoneNumber(call.from_number)}
                </div>
              </div>
              <div className="flex justify-between">
                <Label>To:</Label>
                <div className="flex gap-2">
                  {!call.is_outgoing && (
                    <Tooltip content="Voice Agent" position="top">
                      <Robot className="bold w-5 h-5" />
                    </Tooltip>
                  )}

                  {((!call.is_outgoing &&
                    call.stage_outcome === 'bot_disconnected') ||
                    (call.is_outgoing &&
                      call.stage_outcome === 'human_disconnected')) && (
                    <Tooltip content="Ended the call" position="top">
                      <PhoneSlash className="bold w-5 h-5" />
                    </Tooltip>
                  )}
                  {processPhoneNumber(call.to_number)}
                </div>
              </div>
            </div>
          ),
        };
      }),
    );

    setRows(calls as never[]);
    setTotalItems(fetchResult.total);
    setLoading(false);
  };

  useEffect(() => {
    if (!loading) {
      fetchData(currentPage, size);
    }
  }, [currentPage, filters, sortConfig, size]);

  const handlePageChange = async (page: number, perPage: number) => {
    setCurrentPage(page);
    setSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setSize(perPage);
    setCurrentPage(1);
    fetchData(1, perPage);
  };

  const handleSort = (key: string, direction: string) => {
    setSortConfig({ key, direction });
  };

  const handleApplyFilters = (newFilters: CallFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const searchAgents = useCallback(async (name: string) => {
    const queryObject: QueryObject = {
      ...(name.length !== 0 && { filters: JSON.stringify({ name }) }),
    };
    const query = new URLSearchParams(queryObject as Record<string, string>);

    const data = await get(`/agents?${query.toString()}`);

    setAgents(data.items);

    return data.items.map((agent: Agent) => ({
      value: agent.id,
      label: agent.name,
    }));
  }, []);

  const handleRowClick = (call: Call) => {
    if (call) {
      openSidebar(
        <CallFeedbackSidebar
          call={call}
          isOpen={isCallFeedbackModalOpen}
          onClose={() => setIsCallFeedbackModalOpen(false)}
        />,
        <div className="mx-3 my-2">
          <h3>Call Feedback</h3>
          <p className="text-sm text-neutral-500">{call.id}</p>
        </div>,
        '550px',
      );
    }
  };

  return (
    <Can operation="read" subject={Subject.CALLS}>
      <div className="mb-6">
        <div className="flex gap-4 items-center">
          <div className="w-64">
            <Label htmlFor="agents">Agents</Label>
            <AutoCompleteSelectBox
              defaultValue={
                filters.agent
                  ? {
                      value: filters.agent,
                      label: agentNameCache[filters.agent],
                    }
                  : undefined
              }
              fetchOptions={searchAgents}
              onChange={(value) => setFilters({ ...filters, agent: value })}
              size="medium"
              disabled={loading}
            />
          </div>
          <div className="w-64">
            <Input
              label="Call ID"
              type="text"
              value={filters.call_id || ''}
              onChange={(e) =>
                setFilters({ ...filters, call_id: e.target.value })
              }
            />
          </div>
          <div className="w-64">
            <Input
              label={
                <>
                  Contact Number
                  <Tooltip
                    content="The number of the contact who received or made the call."
                    zIndex={50}
                  >
                    <Info className="w-4 h-4 inline-block ml-1" />
                  </Tooltip>
                </>
              }
              type="text"
              value={filters.contact_number}
              onChange={(e) =>
                setFilters({ ...filters, contact_number: e.target.value })
              }
            />
          </div>
        </div>
      </div>

      <Table
        headers={headers}
        onRowClick={(index: number) => {
          handleRowClick(rawCalls[index]);
        }}
        rows={rows}
        totalItems={totalItems}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        onSort={handleSort}
        loading={loading}
        setSize={handleSizeChange}
        defaultSize={size}
      />

      <CallFiltersModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        onApplyFilters={handleApplyFilters}
        agents={agents}
        searchAgents={searchAgents}
        loading={loading}
      />
    </Can>
  );
};
